import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ExternalLink } from "lucide-react";
import api from "../lib/api";
import Header from "../components/Header";
import Footer from "../components/Footer";
import FirmIcon from "../components/FirmIcon";
import StarRating from "../components/StarRating";
import ChallengeTable from "../components/ChallengeTable";
import Reveal, { SectionLabel } from "../components/Reveal";

const SPECS = [
  ["FTC Score", (f) => f.rating?.toFixed(1)],
  ["Category", (f) => f.category || "CFD"],
  ["Entry From", (f) => f.min_price],
  ["Max Capital", (f) => f.max_capital],
  ["Profit Split", (f) => f.profit_split],
  ["Payout Cycle", (f) => f.payout_frequency],
  ["Total Paid Out", (f) => f.payouts_paid],
  ["Traders Funded", (f) => f.traders_count],
  ["Founded", (f) => f.founded],
  ["Headquarters", (f) => f.headquarters],
  ["Prices Verified", (f) => f.prices_verified],
];

function FirmPicker({ label, value, firms, onChange, testid }) {
  return (
    <label className="block">
      <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-muted">{label}</span>
      <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
        className="mt-2 w-full rounded-xl border border-line bg-ink px-4 py-3.5 font-mono text-xs text-white outline-none focus:border-signal">
        {firms.map((f) => <option key={f.slug} value={f.slug}>{f.name}</option>)}
      </select>
    </label>
  );
}

function FirmColumn({ firm }) {
  return (
    <div data-testid={`compare-col-${firm.slug}`}>
      <div className="card-violet flex items-center justify-between gap-4 p-6">
        <div className="flex items-center gap-4">
          <div className="flex h-14 w-14 items-center justify-center overflow-hidden rounded-xl border border-line bg-elevate">
            <FirmIcon name={firm.icon} size={24} logo={firm.logo} />
          </div>
          <div>
            <Link to={`/firms/${firm.slug}`} className="font-display text-lg font-800 text-white transition-colors hover:text-signal" data-testid={`compare-name-${firm.slug}`}>
              {firm.name}
            </Link>
            <div className="mt-1"><StarRating value={firm.rating} idPrefix={`cmp-${firm.slug}`} /></div>
          </div>
        </div>
        <a href={firm.website_url} target="_blank" rel="noopener noreferrer sponsored" data-testid={`compare-visit-${firm.slug}`}
          className="btn-gradient inline-flex items-center gap-1.5 px-4 py-2.5 font-mono text-[10px] font-700 uppercase tracking-[0.15em] text-white">
          Visit <ExternalLink size={10} />
        </a>
      </div>
      <ChallengeTable firm={firm} label={`${firm.name} — Plans`} testid={`compare-challenges-${firm.slug}`} />
    </div>
  );
}

export default function Compare() {
  const [firms, setFirms] = useState([]);
  const [selA, setSelA] = useState("");
  const [selB, setSelB] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/firms")
      .then(({ data }) => {
        const sorted = [...data].sort((a, b) => b.rating - a.rating);
        setFirms(sorted);
        setSelA(sorted[0]?.slug || "");
        setSelB(sorted[1]?.slug || sorted[0]?.slug || "");
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const firmA = useMemo(() => firms.find((f) => f.slug === selA), [firms, selA]);
  const firmB = useMemo(() => firms.find((f) => f.slug === selB), [firms, selB]);

  return (
    <div data-testid="compare-page">
      <Header />
      <section className="relative overflow-hidden border-b border-line">
        <div className="terminal-grid absolute inset-0 opacity-50" />
        <div className="absolute -left-24 top-0 h-72 w-72 rounded-full bg-signal/15 blur-[130px]" />
        <div className="relative mx-auto max-w-7xl px-5 py-20 lg:px-8">
          <Reveal>
            <SectionLabel>Head To Head</SectionLabel>
            <h1 className="mt-3 font-display text-5xl font-900 tracking-tight text-white sm:text-6xl">
              Compare <span className="text-gradient">firms</span>
            </h1>
            <p className="mt-5 max-w-xl text-base leading-relaxed text-muted">
              Two desks side by side — scores, specs, every challenge account, price and rule. Club codes apply at checkout.
            </p>
          </Reveal>
          <div className="mt-10 grid max-w-3xl gap-4 sm:grid-cols-2" data-testid="compare-pickers">
            <FirmPicker label="Firm A" value={selA} firms={firms} onChange={setSelA} testid="compare-select-a" />
            <FirmPicker label="Firm B" value={selB} firms={firms} onChange={setSelB} testid="compare-select-b" />
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 py-16 lg:px-8">
        {loading ? (
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-muted" data-testid="compare-loading">
            LOADING TAPE<span className="animate-blink">…</span>
          </p>
        ) : !firmA || !firmB ? (
          <p className="rounded-2xl border border-line bg-surface p-10 text-center font-mono text-xs uppercase tracking-[0.2em] text-muted" data-testid="compare-empty">
            Pick two firms to compare.
          </p>
        ) : (
          <>
            <Reveal>
              <div className="overflow-x-auto rounded-2xl border border-line" data-testid="compare-tape">
                <table className="w-full min-w-[620px] border-collapse font-mono text-xs">
                  <thead>
                    <tr className="border-b border-line bg-elevate text-left text-[10px] uppercase tracking-[0.2em] text-muted">
                      <th className="px-5 py-4 font-500">Spec</th>
                      <th className="px-5 py-4 font-500 text-white">{firmA.name}</th>
                      <th className="px-5 py-4 font-500 text-white">{firmB.name}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {SPECS.map(([label, get]) => (
                      <tr key={label} className="border-b border-line/50 text-muted" data-testid={`compare-spec-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                        <td className="px-5 py-3.5 text-[10px] uppercase tracking-[0.18em]">{label}</td>
                        <td className="px-5 py-3.5 font-600 text-signal">{get(firmA) || "—"}</td>
                        <td className="px-5 py-3.5 font-600 text-signal">{get(firmB) || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Reveal>
            <div className="mt-12 grid items-start gap-10 lg:grid-cols-2" data-testid="compare-plans">
              <FirmColumn firm={firmA} />
              <FirmColumn firm={firmB} />
            </div>
          </>
        )}
      </section>
      <Footer />
    </div>
  );
}
